// Wiring for the investigation graph: feeding it from analytics results,
// persisting it per chat, the tools the model uses to read it and to make
// claims against it, and the system-prompt block that carries it across
// runs and compactions.
//
// The graph is the chat's context variable in the RLM sense: it lives
// outside the message history, every hand that runs a query writes to
// it (the root and any leaf sub-agent share the same object through the
// shared tool registry), and the model reads it on demand with
// `graph_state` rather than re-querying what it already knows.

import { Type } from "@earendil-works/pi-ai";
import { browserStorageKey } from "./browserStorageScope.js";
import { alreadyKnown, entityRules, pivotSpec, pivotsFrom, renderAffordances } from "./graphPivot.js";
import { READER_OUTPUT_SCHEMA, READER_SYSTEM_PROMPT, buildPlan, citableKeys, normalizeReaderClaim, readerTask, rowExcerpt } from "./graphExpand.js";
import { graphView } from "./investigationGraphView.js";
import {
    CLAIM_KINDS,
    NODE_KINDS,
    addClaim,
    applyWrites,
    createGraph,
    extractWrites,
    fromJSON,
    renderGraph,
    sliceAround,
    toJSON,
} from "./investigationGraph.js";

export const GRAPH_STORAGE_PREFIX = "origin-investigation-graph-v1";
export const MAX_STORED_GRAPHS = 40;
// Graphs are the largest thing the dashboard keeps in browser storage (one
// busy investigation serializes past a megabyte), and the origin's quota is
// shared with the lessons and recipes stores. Total graph bytes stay under
// this budget, oldest first, so a full store never swallows a recipe.
export const MAX_GRAPH_STORE_CHARS = 2_500_000;
export const GRAPH_PROMPT_BUDGET = 6000;

// Per-chat persistence in the browser, scoped like the other chat-side
// stores. Server-side persistence rides the chat record later; for now a
// reload on the same browser keeps the investigation, a new device does not.
export function createGraphStore({ storage = null, keyFor = (chatId) => { const base = browserStorageKey(GRAPH_STORAGE_PREFIX); return base ? `${base}:${chatId}` : null; }, now = () => Date.now(), maxChars = MAX_GRAPH_STORE_CHARS } = {}) {
    function resolveStorage() {
        if (storage) return storage;
        try { return globalThis.localStorage || null; } catch { return null; }
    }
    function allKeys(s) {
        const keys = [];
        try {
            for (let i = 0; i < (s.length || 0); i++) {
                const k = s.key(i);
                if (k && k.startsWith(GRAPH_STORAGE_PREFIX + ":")) keys.push(k);
            }
        } catch { /* storage without key(): skip pruning */ }
        return keys;
    }
    return {
        load(chatId) {
            const key = chatId ? keyFor(chatId) : null;
            if (!key) return createGraph();
            try {
                const raw = resolveStorage()?.getItem(key);
                return raw ? fromJSON(JSON.parse(raw).graph) : createGraph();
            } catch { return createGraph(); }
        },
        save(chatId, graph) {
            const key = chatId ? keyFor(chatId) : null;
            const s = resolveStorage();
            if (!key || !s) return false;
            try {
                if (!graph.nodes.size && !graph.claims.length) { s.removeItem(key); return true; }
                const payload = JSON.stringify({ version: 1, savedAt: now(), graph: toJSON(graph) });
                // Oldest graphs go first, by count and by total size, keeping
                // room for this one. The browser's own quota is shared with
                // everything else on the origin, so a write it still refuses
                // gives up one more old graph at a time until it lands.
                const oldestFirst = () => allKeys(s).filter((k) => k !== key).map((k) => { const raw = s.getItem(k) || ""; let at = 0; try { at = JSON.parse(raw)?.savedAt || 0; } catch { at = 0; } return { k, at, size: raw.length }; }).sort((a, b) => a.at - b.at);
                const others = oldestFirst();
                let total = others.reduce((n, o) => n + o.size, 0) + payload.length;
                let count = others.length + 1;
                for (const o of others) {
                    if (count <= MAX_STORED_GRAPHS && total <= maxChars) break;
                    s.removeItem(o.k);
                    total -= o.size;
                    count -= 1;
                }
                for (;;) {
                    try { s.setItem(key, payload); return true; } catch (err) {
                        const [oldest] = oldestFirst();
                        if (!oldest) throw err;
                        s.removeItem(oldest.k);
                    }
                }
            } catch { return false; }
        },
        forget(chatId) {
            const key = chatId ? keyFor(chatId) : null;
            if (!key) return;
            try { resolveStorage()?.removeItem(key); } catch { /* nothing to forget */ }
        },
    };
}

// One analytics result -> graph writes. Returns what the write added so
// the tool result can say so in a line, plus the keys of the anchors that
// are new, so the note can name the pivots they unlock.
export function feedGraph(graph, { toolCallId, spec, objects }) {
    const entity = spec?.root?.entity || null;
    if (!entity || !Array.isArray(objects) || !objects.length) return { nodes: 0, edges: 0, claims: 0, newKeys: [] };
    const before = new Set(graph.nodes.keys());
    const writes = extractWrites({ toolCallId, entity, rows: objects, timeRange: spec?.timeRange || null });
    const applied = applyWrites(graph, writes);
    applied.newKeys = writes.nodes.map((n) => `${n.kind}:${n.id}`).filter((k) => !before.has(k));
    return applied;
}

// The moves the result just unlocked, one line per anchor kind, with a
// concrete key to copy. This is where the model meets `pivot`: at the
// moment it holds a new anchor, not in a tool description it read once.
export const EXPAND_ALL_MAX = 12;

// Live graphs, one per open chat, published for the chat UI. The agent
// registers the graph object it mutates and pings after every persist;
// the UI subscribes by chat key and re-reads the same object. Keys are
// the chat id the agent was built with, or "default" before one exists.
const liveGraphs = new Map();
const graphListeners = new Map();
const graphVersions = new Map();

function bumpGraph(chatKey) {
    graphVersions.set(chatKey, (graphVersions.get(chatKey) || 0) + 1);
    for (const cb of graphListeners.get(chatKey) || []) {
        try { cb(); } catch { /* a listener must not break the agent */ }
    }
}

// The chat the console is showing. Blocks in the transcript use it to
// read the live graph for text a snapshot had to shorten.
let activeGraphChatKey = "default";
export function setActiveGraphChat(chatId) {
    activeGraphChatKey = graphKeyFor(chatId);
}
export function activeGraphKey() {
    return activeGraphChatKey;
}

// The persistent chat id when the chat has one (local ids are a per-tab
// sequence and change on reload), else the local id, else "default".
export function graphKeyFor(chatId, serverChatId = null) {
    if (serverChatId != null && serverChatId !== "") return String(serverChatId);
    return chatId == null || chatId === "" ? "default" : String(chatId);
}

// A closed chat releases its graph (piChat exposes the disposer as
// agent.releaseGraph and the console calls it at teardown); the cap is
// the safety net for any path that forgets, evicting the oldest entry.
export const MAX_LIVE_GRAPHS = 24;

export function releaseLiveGraph(chatKey) {
    if (!liveGraphs.has(chatKey)) return false;
    liveGraphs.delete(chatKey);
    bumpGraph(chatKey);
    return true;
}

export function liveGraphCount() {
    return liveGraphs.size;
}

export function registerLiveGraph(chatKey, graph) {
    if (!graph) return () => {};
    liveGraphs.delete(chatKey);
    liveGraphs.set(chatKey, graph);
    while (liveGraphs.size > MAX_LIVE_GRAPHS) {
        const oldest = liveGraphs.keys().next().value;
        liveGraphs.delete(oldest);
        bumpGraph(oldest);
    }
    bumpGraph(chatKey);
    return () => {
        if (liveGraphs.get(chatKey) === graph) releaseLiveGraph(chatKey);
    };
}

export function notifyGraphChanged(chatKey) {
    bumpGraph(chatKey);
}

export function subscribeGraph(chatKey, cb) {
    if (!graphListeners.has(chatKey)) graphListeners.set(chatKey, new Set());
    graphListeners.get(chatKey).add(cb);
    return () => { graphListeners.get(chatKey)?.delete(cb); };
}

export function graphVersion(chatKey) {
    return graphVersions.get(chatKey) || 0;
}

// The live graph when the chat is open in this tab, else what the
// browser store holds for it (a reopened chat before its agent exists).
export function readLiveGraph(chatKey, { store = null } = {}) {
    const live = liveGraphs.get(chatKey);
    if (live) return live;
    return (store || createGraphStore()).load(chatKey === "default" ? null : chatKey);
}

// Everything an agent needs to hold its chat's graph: load it from the
// browser store, publish it live, persist and ping on change, release on
// close. piChat wires the pieces onto the agent; the behaviour lives here
// so it can be tested without building an agent.
// A brand-new chat's agent can be built before the console has recorded
// the chat's persistent id, so `getServerChatId` is consulted again on
// every persist: the first time it answers, the graph moves to that key
// (registry and store), and the old key is pinged so a reader on it
// re-resolves.
export function attachLiveGraph({ chatId, serverChatId = null, getServerChatId = null, store = createGraphStore() }) {
    let currentServerId = serverChatId || null;
    let storeId = currentServerId || chatId || null;
    let graphKey = graphKeyFor(chatId, currentServerId);
    const graph = store.load(storeId);
    let release = registerLiveGraph(graphKey, graph);
    const rekeyIfNeeded = () => {
        let latest = null;
        try { latest = getServerChatId?.() || null; } catch { latest = null; }
        if (!latest || latest === currentServerId) return;
        const oldKey = graphKey;
        const oldStoreId = storeId;
        release();
        currentServerId = latest;
        storeId = latest;
        graphKey = graphKeyFor(chatId, latest);
        release = registerLiveGraph(graphKey, graph);
        if (oldStoreId !== storeId) { try { store.forget?.(oldStoreId); } catch { /* nothing to forget */ } }
        notifyGraphChanged(oldKey);
    };
    return {
        graph,
        get graphKey() { return graphKey; },
        persistGraph: () => {
            rekeyIfNeeded();
            store.save(storeId, graph);
            notifyGraphChanged(graphKey);
        },
        releaseGraph: () => release(),
    };
}

export function graphSummary(graph) {
    const byKind = {};
    for (const n of graph.nodes.values()) byKind[n.kind] = (byKind[n.kind] || 0) + 1;
    const supported = graph.claims.filter((c) => c.status === "supported").length;
    return { nodes: graph.nodes.size, byKind, claims: { total: graph.claims.length, supported, insufficient: graph.claims.length - supported } };
}

// What a tool result carries for the UI: the graph as it stood right
// after that call, small enough to persist with the chat.
export const SNAPSHOT_RENDER_BUDGET = 2500;
export function graphSnapshot(graph, { newKeys = [], budget = SNAPSHOT_RENDER_BUDGET, changed = null } = {}) {
    const didChange = changed == null ? newKeys.length > 0 : Boolean(changed);
    // The full count of what the call added and its split by kind survive
    // the cap on the listed keys, so a call that added 128 processes says so.
    const addedByKind = {};
    for (const k of newKeys) { const kind = k.slice(0, k.indexOf(":")); if (kind) addedByKind[kind] = (addedByKind[kind] || 0) + 1; }
    return {
        kind: "investigationGraph",
        ...graphSummary(graph),
        newKeys: newKeys.slice(0, EXPAND_ALL_MAX),
        added: newKeys.length,
        addedByKind,
        changed: didChange,
        // The drawable slice and the model's text only when the call moved
        // the graph; unchanged calls carry the counts alone.
        view: didChange ? graphView(graph, { highlight: newKeys }) : null,
        render: didChange ? renderGraph(graph, { budget }) : null,
    };
}

export function pivotHints(graph, applied, schema, { maxKinds = 3 } = {}) {
    if (!schema || !applied?.newKeys?.length) return [];
    const byKind = new Map();
    for (const key of applied.newKeys) {
        const node = graph.nodes.get(key);
        if (!node || node.kind === "window" || node.kind === "host") continue;
        if (!byKind.has(node.kind)) byKind.set(node.kind, node);
    }
    const lines = [];
    for (const [kind, node] of byKind) {
        const targets = pivotsFrom(schema, node);
        if (!targets.length) continue;
        const keys = applied.newKeys.filter((k) => k.startsWith(kind + ":"));
        const count = keys.length;
        const expandable = kind === "session" || kind === "endpoint" || kind === "process";
        let move;
        if (expandable && count >= 2) {
            move = `expand_all { anchors: ${JSON.stringify(keys.slice(0, EXPAND_ALL_MAX))}, question } investigates them in one call (one reader each)${count > EXPAND_ALL_MAX ? `; graph_state { kind: "${kind}" } lists the rest` : ""}`;
        } else if (expandable) {
            move = `expand { anchor: "${node.key}", question, term } runs ${targets.slice(0, 4).map((p) => p.entity + (p.needsTerm ? " (term)" : "")).join(", ")} for you and returns claims; or pivot { from: "${node.key}", to: ... }`;
        } else {
            move = `pivot { from: "${node.key}", to: ${targets.slice(0, 5).map((p) => p.entity + (p.needsTerm ? " (term)" : "")).join(" | ")} }`;
        }
        lines.push(`${count} new ${kind}${count === 1 ? "" : "s"}: ${move}`);
        if (lines.length >= maxKinds) break;
    }
    return lines;
}

export function deltaNote(applied, graph, schema = null) {
    const parts = [];
    if (applied.nodes) parts.push(`+${applied.nodes} anchor${applied.nodes === 1 ? "" : "s"}`);
    if (applied.edges) parts.push(`+${applied.edges} relation${applied.edges === 1 ? "" : "s"}`);
    const total = graph.nodes.size;
    if (!parts.length) return total ? `investigation graph unchanged (${total} anchors known); graph_state shows it.` : null;
    const hints = pivotHints(graph, applied, schema);
    const head = `investigation graph: ${parts.join(", ")}; ${total} anchor${total === 1 ? "" : "s"} known. graph_state shows them; cite their keys in claim.`;
    return hints.length ? `${head}\n${hints.join("\n")}` : head;
}

const LESSONS_LIKE_BLOCK = /\n*<investigation_graph>[\s\S]*?<\/investigation_graph>/;

export function replaceGraphBlock(systemPrompt, render) {
    const prompt = String(systemPrompt || "");
    const clean = render ? `\n\n${render}` : "";
    if (LESSONS_LIKE_BLOCK.test(prompt)) return prompt.replace(LESSONS_LIKE_BLOCK, clean);
    return clean ? prompt + clean : prompt;
}

const stateSchema = Type.Object({
    key: Type.Optional(Type.String({ description: "A node key such as `session:<id>`; returns that anchor's neighbourhood instead of the whole graph." })),
    hops: Type.Optional(Type.Number({ description: "Neighbourhood radius when `key` is set. Default 1." })),
    kind: Type.Optional(Type.String({ description: `Limit to one anchor kind: ${NODE_KINDS.join(", ")}.` })),
});

const claimSchema = Type.Object({
    kind: Type.String({ description: `One of ${CLAIM_KINDS.join(", ")}. Use attribution for "who ran X / on which machine / in which session".` }),
    statement: Type.String({ description: "One sentence stating the conclusion." }),
    evidence: Type.Optional(Type.Array(Type.String(), { description: "Node keys from graph_state that support it, e.g. [\"session:<id>\", \"endpoint:<id>\"]. Without it the claim is recorded as insufficient, not refused." })),
    subject: Type.Optional(Type.String({ description: "The node key the claim is about." })),
    id: Type.Optional(Type.String({ description: "Re-state an earlier claim by passing its id." })),
});

function text(payload) {
    return { content: [{ type: "text", text: typeof payload === "string" ? payload : JSON.stringify(payload) }], details: typeof payload === "string" ? null : payload };
}

export function createGraphTools({ graph, persist = () => {}, getSchema = null }) {
    const stateTool = {
        name: "graph_state",
        label: "Investigation graph",
        description:
            "What this investigation has established so far: anchors (endpoints, sessions, processes, prompts, messages, people, windows, hosts) with the evidence class of the records that produced them, the relations between them, and the claims made. Every analytics_query result feeds it, including results fetched by sub-agents, so read it before re-querying something you already have. Pass `key` for one anchor's neighbourhood.",
        parameters: stateSchema,
        async execute(_toolCallId, { key, hops, kind } = {}) {
            if (key) {
                if (!graph.nodes.has(key)) return text(`no anchor ${key} in the graph`);
                return text(renderGraph(sliceAround(graph, key, { hops: Math.max(1, Math.min(3, Number(hops) || 1)) }), { budget: GRAPH_PROMPT_BUDGET }));
            }
            if (kind) {
                const sub = createGraph();
                for (const n of graph.nodes.values()) if (n.kind === kind) sub.nodes.set(n.key, n);
                sub.claims = graph.claims;
                if (!sub.nodes.size) return text(`no ${kind} anchors yet`);
                return text(renderGraph(sub, { budget: GRAPH_PROMPT_BUDGET }));
            }
            const body = renderGraph(graph, { budget: GRAPH_PROMPT_BUDGET });
            const schema = getSchema?.();
            const moves = schema && graph.nodes.size ? renderAffordances(schema, graph) : "";
            return text(moves ? `${body}\n${moves}` : body);
        },
    };
    const claimTool = {
        name: "claim",
        label: "Claim",
        description:
            "Record a conclusion against the investigation graph, citing the node keys that support it. An attribution (who ran X, on which machine, in which session) is only supported when a cited anchor came from an execution record (PROCESS_EVENT, TELEMETRY_TIMELINE, TRACE_TOOL_ACTIVITY) or from the model's own tool call in the conversation capture; prompt summaries and directory rows describe intent and ownership, not what ran. An insufficient claim is recorded with the reason so you can go and get the evidence. Make your claims before you write the final answer, and build the answer from them.",
        parameters: claimSchema,
        async execute(toolCallId, args) {
            const evidence = Array.isArray(args?.evidence) ? args.evidence.filter((k) => typeof k === "string") : [];
            const result = addClaim(graph, { ...args, evidence, source: { toolCallId } });
            persist();
            return {
                ...text({ status: result.status, reason: result.reason, claim: result.claim || null }),
                details: { kind: "claim", status: result.status, reason: result.reason, claimId: result.claim?.id || null, graph: graphSnapshot(graph, { changed: result.added }) },
            };
        },
    };
    return [stateTool, claimTool];
}

// The brief a leaf sub-agent starts from when the parent points it at one
// anchor: the anchor's neighbourhood and the contract for writing back.
// Children share the parent's graph_state and claim tools by reference,
// so what they establish lands in the same graph with their own tool
// call ids as provenance; nothing has to be merged by hand.
export function anchorBrief(graph, key, { hops = 1 } = {}) {
    if (!graph || !key) return null;
    if (!graph.nodes.has(key)) return `Anchor ${key} is not in the investigation graph yet; establish it with a query before relying on it.`;
    const slice = renderGraph(sliceAround(graph, key, { hops: Math.max(1, Math.min(3, hops)) }), { budget: 3000 });
    return [
        `Your anchor is ${key}. What the investigation already holds around it:`,
        slice,
        "Work from this anchor. Every analytics_query you run is folded into the shared investigation graph automatically; record what you conclude with `claim`, citing anchor keys, so the parent sees it. Do not re-query anchors listed above unless you need their rows.",
    ].join("\n");
}

const pivotSchema = Type.Object({
    from: Type.String({ description: "Anchor key from graph_state, e.g. `session:<trace id>` or `endpoint:<id>`." }),
    to: Type.String({ description: "Target entity, e.g. PROCESS_EVENT, TELEMETRY_TIMELINE, TRACE_TOOL_ACTIVITY, CONVERSATION_SEARCH_CONTENT, CONVERSATION_MESSAGE, DNS_EVENT, FILE_EVENT, NETWORK_CONNECTION, PROMPT_SEARCH, ENDPOINT. graph_state lists what each anchor kind can pivot into." }),
    term: Type.Optional(Type.String({ description: "Text to match in the entity's text field (command line, tool input, search text, path, query name). Required for the search views. Case-insensitive; wildcards are added for you." })),
    window: Type.Optional(Type.Unknown({ description: "Either { start, end } in RFC 3339 UTC, or a string like \"7d\". Default: the window the anchor was observed in, padded; conversation entities use their widest window because their rows carry the trace's start time." })),
    limit: Type.Optional(Type.Number({ description: "Rows to return; clamped to the entity's bound (200 for search views, 1000 otherwise). Default 200." })),
    fields: Type.Optional(Type.Array(Type.String(), { description: "Field names to select instead of the entity's defaults." })),
});

// pivot: the correct query from a known anchor into a target entity,
// built by the harness. Runs through the same analytics_query path, so
// memory hints, the result store, and the graph feed all apply, and the
// spec it used is returned so the model learns the shape by example.
export function createPivotTool({ graph, getSchema, analyticsTool }) {
    return {
        name: "pivot",
        label: "Pivot",
        description:
            "Query a target entity from an anchor you already hold, without writing the spec. Give an anchor key from graph_state and a target entity; the harness picks the id field to match, the entity's time field, window and row bounds, and the search field for a term, then runs the query and folds the rows into the graph. Use it for the common investigation moves: a session's processes, tool calls, or conversation content; an endpoint's timeline or processes in a window; a process's DNS, file, or network activity; a person's processes. The result includes the spec that ran, so copy its shape when you need a query pivot does not offer.",
        parameters: pivotSchema,
        async execute(toolCallId, args, signal, onUpdate) {
            const anchor = graph.nodes.get(args.from);
            if (!anchor) {
                const near = [...graph.nodes.keys()].filter((k) => k.includes(String(args.from).split(":").pop() || "")).slice(0, 5);
                return text(`no anchor ${args.from} in the graph${near.length ? `; did you mean ${near.join(", ")}` : "; graph_state lists what is held"}`);
            }
            const schema = getSchema?.();
            if (!schema) return text("the analytics schema is not loaded, so pivots cannot be built; use analytics_query");
            let built;
            try {
                built = pivotSpec({ schema, graph, anchor, to: args.to, term: args.term, window: args.window, limit: args.limit, fields: args.fields });
            } catch (err) {
                const legal = pivotsFrom(schema, anchor).map((p) => p.entity + (p.needsTerm ? " (term)" : ""));
                return text(`pivot not built: ${err?.message || err}. From ${anchor.key} you can pivot into: ${legal.join(", ")}`);
            }
            const known = alreadyKnown(graph, anchor, built.entity);
            const header = [
                `Pivot ${anchor.key} -> ${built.entity} on ${built.idField}${args.term ? ` matching ${JSON.stringify(String(args.term))}` : ""}.`,
                built.notes.length ? built.notes.join("; ") + "." : null,
                known.length ? `Already held before this call: ${known.length} anchor${known.length === 1 ? "" : "s"} from ${built.entity} linked to ${anchor.key}; the rows below may repeat them.` : null,
                `Spec used: ${JSON.stringify(built.spec)}`,
            ].filter(Boolean).join("\n");
            let result;
            try {
                result = await analyticsTool.execute(toolCallId, { spec: built.spec }, signal, onUpdate);
            } catch (err) {
                const wrapped = err instanceof Error ? err : new Error(String(err));
                try { wrapped.message = `${wrapped.message}\n(pivot spec: ${JSON.stringify(built.spec)})`; } catch { /* frozen */ }
                throw wrapped;
            }
            // querySpec is the query this pivot ran for the model: the run
            // trace and the kernel extractor read it, since the call's own
            // arguments carry no spec.
            return {
                ...result,
                content: [{ type: "text", text: header }, ...(result.content || [])],
                details: { ...(result.details || {}), querySpec: built.spec },
            };
        },
    };
}

// Anchors a hand-written spec refers to: any equality predicate whose value
// is the id of a node the graph holds.
function specAnchors(graph, spec) {
    const found = new Set();
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 32) return;
        if (Array.isArray(node)) { for (const child of node) walk(child, depth + 1); return; }
        if (node.pred && typeof node.pred.value === "string") {
            for (const n of graph.nodes.values()) if (n.kind !== "window" && n.id === node.pred.value) found.add(n);
        }
        for (const value of Object.values(node)) walk(value, depth + 1);
    };
    walk(spec?.root?.stepFilter, 0);
    walk(spec?.filter, 0);
    return [...found];
}

// What to append to a failing analytics_query: the entity's rules as pivot
// applies them and, when the spec names an anchor the graph holds, the
// pivot and expand calls that would have built it. Null when the entity is
// not one pivot serves, so unrelated failures stay untouched.
export function graphHintForError({ graph, schema, spec }) {
    const entity = typeof spec?.root?.entity === "string" ? spec.root.entity.replace(/^ENTITY_TYPE_/, "") : null;
    if (!entity || !schema || !graph) return null;
    const rules = entityRules(entity);
    if (!rules.length) return null;
    const lines = [`Investigation graph: ${entity} rules the harness applies in pivot: ${rules.join("; ")}.`];
    const anchor = specAnchors(graph, spec).find((n) => pivotsFrom(schema, n).some((p) => p.entity === entity));
    if (anchor) {
        lines.push(`You hold ${anchor.key}: pivot { from: "${anchor.key}", to: "${entity}", term } builds this query with those rules applied, and expand { anchor: "${anchor.key}", question, term } runs its standard set.`);
    }
    return lines.join("\n");
}

export function withGraphHints(tool, { graph, getSchema }) {
    if (!tool || typeof tool.execute !== "function" || !graph) return tool;
    return {
        ...tool,
        async execute(toolCallId, args, ...rest) {
            try {
                return await tool.execute(toolCallId, args, ...rest);
            } catch (err) {
                let hint = null;
                try { hint = graphHintForError({ graph, schema: getSchema?.(), spec: args?.spec }); } catch { hint = null; }
                if (!hint) throw err;
                const augmented = err instanceof Error ? err : new Error(String(err));
                try { augmented.message = `${augmented.message}\n\n${hint}`; } catch { /* frozen message: surface the bare error */ }
                throw augmented;
            }
        },
    };
}

async function mapBounded(items, limit, fn) {
    const out = new Array(items.length);
    let next = 0;
    const workers = new Array(Math.max(1, Math.min(limit, items.length))).fill(0).map(async () => {
        while (next < items.length) {
            const i = next++;
            out[i] = await fn(items[i], i);
        }
    });
    await Promise.all(workers);
    return out;
}

export function rowsFromContent(out) {
    let payload = {};
    try { payload = JSON.parse(out?.content?.find((c) => c?.type === "text")?.text || "{}"); } catch { payload = {}; }
    const columns = Array.isArray(payload.columns) ? payload.columns : [];
    const rows = (Array.isArray(payload.rows) ? payload.rows : []).map((row) => {
        const values = Array.isArray(row?.values) ? row.values : [];
        return Object.fromEntries(columns.map((c, i) => [c, values[i]]));
    });
    return { rows, total: payload._preview?.total ?? rows.length };
}

// expand: the harness runs an anchor's standard pivots itself, feeds the
// graph through the analytics tool's own observer, then hands the slice and
// the fetched rows to a stateless task-model reader whose only output is
// claims. One root call, no improvised specs, and the leaf is the paper's
// llm_query: slice in, structure out. `runLeaf` is injected so this module
// stays free of the sub-agent runtime.
// The reader's transcript as the chat keeps it: the same message shape the
// sub-agent transcript view reads, with long texts clipped so a chat that
// ran many expands stays a sane size.
export const READER_TRANSCRIPT_MESSAGES = 12;
export const READER_TRANSCRIPT_CLIP = 2500;
export function compactReaderMessages(messages) {
    const clip = (s) => (typeof s === "string" && s.length > READER_TRANSCRIPT_CLIP ? `${s.slice(0, READER_TRANSCRIPT_CLIP)}\u2026 [${s.length - READER_TRANSCRIPT_CLIP} more chars]` : s);
    return (Array.isArray(messages) ? messages : []).slice(-READER_TRANSCRIPT_MESSAGES).map((m) => {
        if (!m || typeof m !== "object") return m;
        if (typeof m.content === "string") return { ...m, content: clip(m.content) };
        if (!Array.isArray(m.content)) return m;
        return {
            ...m,
            content: m.content.map((b) => {
                if (!b || typeof b !== "object") return b;
                if (typeof b.text === "string") return { ...b, text: clip(b.text) };
                if (b.type === "toolCall") return { ...b, arguments: clip(typeof b.arguments === "string" ? b.arguments : JSON.stringify(b.arguments ?? {})) };
                return b;
            }),
        };
    });
}

// What the tool row says while an expand runs and after it finishes.
export function expandProgressLabel(details) {
    if (!details) return null;
    if (details.kind === "expand_all") {
        const results = Array.isArray(details.results) ? details.results : [];
        const done = results.filter((r) => r && (r.phase === "done" || r.phase === "error" || r.error)).length;
        if (details.phase && details.phase !== "done") return `expanding ${done}/${details.total ?? results.length} anchors\u2026`;
        const claims = results.reduce((n, r) => n + (r?.reader?.claims?.length || 0), 0);
        const context = results.reduce((n, r) => n + (r?.reader?.claims || []).filter((c) => c?.bearing === "context").length, 0);
        return `${results.length} anchor${results.length === 1 ? "" : "s"}, ${claims} claim${claims === 1 ? "" : "s"}${context ? ` (${context} context)` : ""}`;
    }
    if (details.kind !== "expand") return null;
    if (details.error) return `error: ${String(details.error).slice(0, 80)}`;
    if (details.phase === "pending") return "queued\u2026";
    const steps = Array.isArray(details.steps) ? details.steps : [];
    if (details.phase === "pivots") return `running pivots ${steps.length}/${details.planned ?? steps.length}\u2026`;
    if (details.phase === "reading") return `reading ${steps.reduce((n, s) => n + (s.rows || 0), 0)} rows\u2026`;
    const claims = details.reader?.claims?.length || 0;
    const context = (details.reader?.claims || []).filter((c) => c?.bearing === "context").length;
    const errors = steps.filter((s) => s.error).length;
    return `${steps.length} pivot${steps.length === 1 ? "" : "s"}${errors ? ` (${errors} failed)` : ""}, ${details.reader ? `${claims} claim${claims === 1 ? "" : "s"}${context ? ` (${context} context)` : ""}` : "no reader"}`;
}

export function makeEmitter(onUpdate, getDetails, { intervalMs = 150 } = {}) {
    let timer = null;
    let dirty = false;
    const fire = () => { try { onUpdate?.({ details: getDetails() }); } catch { /* progress is best-effort */ } };
    return () => {
        if (!onUpdate) return;
        if (intervalMs <= 0) { fire(); return; }
        if (timer) { dirty = true; return; }
        fire();
        timer = setTimeout(() => { timer = null; if (dirty) { dirty = false; fire(); } }, intervalMs);
    };
}

export function createExpandTools({ graph, getSchema, analyticsTool, runLeaf = null, getRows = null, persist = () => {}, concurrency = 3, getChildRegistry = null, emitIntervalMs = 150 }) {
    async function expandOne(toolCallId, anchorKey, { question = null, term = null, plan = null, window = null, limit = null, read = true } = {}, signal, onUpdate) {
        const anchor = graph.nodes.get(anchorKey);
        if (!anchor) return { kind: "expand", anchor: anchorKey, phase: "error", error: `no anchor ${anchorKey} in the graph; graph_state lists what is held`, steps: [], skipped: [] };
        const schema = getSchema?.();
        if (!schema) return { kind: "expand", anchor: anchorKey, phase: "error", error: "the analytics schema is not loaded, so pivots cannot be built", steps: [], skipped: [] };
        const { steps, skipped } = buildPlan({ schema, graph, anchor, plan, term, window, limit });
        // Live state the chat row renders while this runs; the same object,
        // completed, is the tool's details when it returns.
        const state = { kind: "expand", anchor: anchor.key, phase: "pivots", planned: steps.length, steps: [], skipped, added: 0, newByKind: {}, reader: null };
        const emit = makeEmitter(onUpdate, () => ({ ...state, steps: state.steps.slice(), reader: state.reader ? { ...state.reader, claims: state.reader.claims.slice() } : null }), { intervalMs: emitIntervalMs });
        emit();
        const before = graph.nodes.size;
        const known = new Set(graph.nodes.keys());
        const results = await mapBounded(steps, concurrency, async (step, i) => {
            const id = `${toolCallId}:${i + 1}`;
            let outcome;
            try {
                const out = await analyticsTool.execute(id, { spec: step.spec }, signal);
                // The inline content carries only a preview of the newest
                // rows; the reader needs the whole result, which the wrapped
                // tool has already recorded under this call id.
                const stored = getRows?.(id);
                const preview = rowsFromContent(out);
                const objects = Array.isArray(stored) ? stored : preview.rows;
                const total = Array.isArray(stored) ? stored.length : preview.total;
                outcome = { to: step.to, idField: step.idField, toolCallId: id, total, objects, error: null };
            } catch (err) {
                outcome = { to: step.to, idField: step.idField, toolCallId: id, total: 0, objects: [], error: err?.message || String(err) };
            }
            state.steps.push({ to: outcome.to, rows: outcome.total, error: outcome.error, toolCallId: outcome.toolCallId, spec: step.spec });
            emit();
            return outcome;
        });
        state.added = graph.nodes.size - before;
        for (const key of graph.nodes.keys()) {
            if (known.has(key)) continue;
            const kind = key.slice(0, key.indexOf(":"));
            if (kind === "window") continue;
            (state.newByKind[kind] ||= []).push(key);
        }
        if (read && runLeaf && results.some((r) => r.total > 0)) {
            const task = readerTask({ anchor, question, sliceText: renderGraph(sliceAround(graph, anchor.key), { budget: 3000 }), excerpt: rowExcerpt(results, { term, question }), keys: citableKeys(graph, anchor, results) });
            const startedAt = Date.now();
            // The reader is a sub-agent the model never spawned itself, so
            // the row shows it the way spawned children are shown: model,
            // turns, transcript, and while it runs a registry entry the
            // supervisor tools can inspect.
            state.phase = "reading";
            state.reader = { status: "running", model: null, turns: 0, claims: [], notes: null, task, systemPrompt: READER_SYSTEM_PROMPT, messages: [] };
            emit();
            const registry = getChildRegistry?.() || null;
            const readerId = `${toolCallId}:reader`;
            let liveMessages = null;
            try {
                const out = await runLeaf({
                    task,
                    systemPrompt: READER_SYSTEM_PROMPT,
                    outputSchema: READER_OUTPUT_SCHEMA,
                    signal,
                    onChildReady: (child) => {
                        state.reader.model = child?.state?.model?.id || null;
                        liveMessages = child?.state?.messages || null;
                        state.reader.messages = liveMessages || [];
                        try {
                            registry?.add(readerId, {
                                kind: "reader",
                                toolCallId,
                                child,
                                promise: null,
                                state: state.reader,
                                taskSummary: `reader for ${anchor.key}`,
                                abort: () => { try { child?.abort?.(); } catch { /* already finished */ } },
                            });
                        } catch { /* registry is optional */ }
                        emit();
                    },
                    onEvent: () => {
                        state.reader.turns = (liveMessages || []).filter((m) => m?.role === "assistant").length;
                        emit();
                    },
                });
                const claims = Array.isArray(out?.result?.claims) ? out.result.claims : [];
                const recorded = claims.map((c) => addClaim(graph, { ...normalizeReaderClaim(c), source: { toolCallId, leaf: true } }));
                const messages = Array.isArray(out?.messages) && out.messages.length ? out.messages : (liveMessages || []);
                state.reader = {
                    ...state.reader,
                    status: out?.status || "unknown",
                    turns: messages.filter((m) => m?.role === "assistant").length,
                    elapsedMs: Date.now() - startedAt,
                    notes: typeof out?.result?.notes === "string" ? out.result.notes.slice(0, 600) : null,
                    // The evidence keys stay on the claim: a recipe counts a
                    // run as a success only when a supported claim cites the
                    // anchor it expanded.
                    claims: recorded.map((res) => (res.claim
                        ? { id: res.claim.id, kind: res.claim.kind, status: res.claim.status, bearing: res.claim.bearing, statement: res.claim.statement, reason: res.claim.reason, evidence: Array.isArray(res.claim.evidence) ? res.claim.evidence.slice() : [] }
                        : { status: res.status, reason: res.reason })),
                    messages: compactReaderMessages(messages),
                };
            } catch (err) {
                state.reader = { ...state.reader, status: "error", error: err?.message || String(err), elapsedMs: Date.now() - startedAt, claims: [], messages: compactReaderMessages(liveMessages || []) };
            } finally {
                try { registry?.remove(readerId); } catch { /* registry is optional */ }
            }
        }
        state.slice = anchorSliceLines(graph, anchor.key);
        state.phase = "done";
        persist();
        emit();
        return state;
    }

// The anchor's identity as the graph holds it: its own line and its
// neighbours (endpoint, person, provider, window). After every expand the
// model queried these again (the endpoint's hostname and user, the
// session's provider, the account); the result now says them.
function anchorSliceLines(graph, key, { max = 7 } = {}) {
    try {
        const text = renderGraph(sliceAround(graph, key, { hops: 1 }), { budget: 2400 });
        const lines = text.split("\n").filter((l) => /^  - /.test(l)).map((l) => l.replace(/^  - /, ""));
        const own = lines.filter((l) => l.startsWith(key + " ") || l === key);
        const rest = lines.filter((l) => !own.includes(l) && !/^process:/.test(l));
        return [...own, ...rest].slice(0, max);
    } catch { return []; }
}

    function summarize(res) {
        if (res.error) return `expand ${res.anchor}: ${res.error}`;
        const lines = [`expand ${res.anchor}: ${res.steps.length} pivot${res.steps.length === 1 ? "" : "s"} run by the harness${res.skipped.length ? `, ${res.skipped.length} skipped` : ""}.`];
        for (const s of res.steps) lines.push(`  - ${s.to}: ${s.error ? `error: ${String(s.error).split("\n")[0].slice(0, 160)}` : `${s.rows} row${s.rows === 1 ? "" : "s"} (tool_call_id ${s.toolCallId})`}`);
        for (const s of res.skipped) lines.push(`  - ${s.to || "plan"}: skipped, ${s.reason}`);
        if (Array.isArray(res.slice) && res.slice.length) { lines.push("who/where, as the graph holds it:"); for (const l of res.slice) lines.push(`  ${l}`); }
        lines.push(`graph: +${res.added} anchor${res.added === 1 ? "" : "s"}. Rows are stored under the tool_call_ids above (expand_preview / from_tool_calls).`);
        for (const [kind, keys] of Object.entries(res.newByKind || {})) {
            if (kind === "process" && keys.length > 6) { lines.push(`  new processes: ${keys.length} (graph_state { kind: "process" } lists them)`); continue; }
            lines.push(`  new ${kind}${keys.length === 1 ? "" : "s"}: ${keys.slice(0, EXPAND_ALL_MAX).join(", ")}${keys.length > EXPAND_ALL_MAX ? ` and ${keys.length - EXPAND_ALL_MAX} more` : ""}`);
            if ((kind === "session" || kind === "endpoint") && keys.length >= 2) lines.push(`  -> expand_all { anchors: ${JSON.stringify(keys.slice(0, EXPAND_ALL_MAX))}, question } investigates them in one call.`);
        }
        if (res.reader) {
            if (res.reader.error) lines.push(`reader: error: ${res.reader.error}`);
            else {
                const context = res.reader.claims.filter((c) => c.bearing === "context").length;
                lines.push(`reader (task model, ${res.reader.status}): ${res.reader.claims.length} claim${res.reader.claims.length === 1 ? "" : "s"}${context ? ` (${context} context, not answering the question)` : ""}`);
                for (const c of res.reader.claims) lines.push(`  - ${c.id || "-"} ${c.kind || ""} ${c.status}${c.bearing === "context" ? " [context]" : ""}: ${c.statement || c.reason || ""}${c.status === "insufficient" && c.reason ? ` (${c.reason})` : ""}`);
                if (res.reader.notes) lines.push(`  notes: ${res.reader.notes}`);
            }
        } else if (res.steps.every((s) => !s.rows)) {
            lines.push("reader skipped: no rows came back.");
        }
        return lines.join("\n");
    }

    const common = {
        question: Type.Optional(Type.String({ description: "What to establish about the anchor(s). The reader answers it with claims." })),
        term: Type.Optional(Type.String({ description: "Text to match where a step searches text (command lines, tool inputs, conversation content, timeline). Enables the term-bound steps of the preset." })),
        plan: Type.Optional(Type.Array(Type.String(), { description: "Entities to pivot into instead of the preset, e.g. [\"PROCESS_EVENT\", \"DNS_EVENT\"]. At most 6." })),
        window: Type.Optional(Type.Unknown({ description: "{ start, end } in RFC 3339 UTC or a string like \"7d\"; default is where the anchor was observed, padded." })),
        limit: Type.Optional(Type.Number({ description: "Rows per pivot; clamped to each entity's bound." })),
        read: Type.Optional(Type.Boolean({ description: "Run the reader leaf after the pivots (default true). Pass false to fetch only." })),
    };

    const expandTool = {
        // Programmatic entry for recipes: same state machine as the tool,
        // without going through the model's tool call.
        run: (toolCallId, anchorKey, args, signal, onUpdate) => expandOne(toolCallId, anchorKey, args || {}, signal, onUpdate),
        name: "expand",
        label: "Expand anchor",
        description:
            "Investigate one anchor in a single call. The harness runs the standard pivots for its kind (a session: processes, tool activity, trace, and conversation content when a term is given; an endpoint: its record, processes, and timeline for a term; a process: DNS, files, connections, tool activity; a person: processes), folds the rows into the graph, then a small reader model turns the rows into claims against the question. Use it instead of writing those queries yourself or spawning a sub-agent per anchor. Returns per-pivot row counts with tool_call_ids for the stored rows, and the reader's claims with their gate status.",
        parameters: Type.Object({ anchor: Type.String({ description: "Anchor key from graph_state, e.g. `session:<trace id>`." }), ...common }),
        async execute(toolCallId, args, signal, onUpdate) {
            const res = await expandOne(toolCallId, args.anchor, args, signal, onUpdate);
            const newKeys = Object.values(res.newByKind || {}).flat();
            return { content: [{ type: "text", text: summarize(res) }], details: { ...res, kind: "expand", graph: graphSnapshot(graph, { newKeys }) } };
        },
    };
    const expandAllTool = {
        name: "expand_all",
        label: "Expand anchors",
        description:
            "expand over a list of anchors with bounded concurrency: one reader per anchor, all writing to the same graph. Use it to fan an investigation out across the sessions or endpoints a search returned.",
        parameters: Type.Object({ anchors: Type.Array(Type.String(), { description: "Anchor keys from graph_state. At most 12." }), ...common }),
        async execute(toolCallId, args, signal, onUpdate) {
            const anchors = (args.anchors || []).slice(0, 12);
            const live = anchors.map((key) => ({ kind: "expand", anchor: key, phase: "pending", steps: [], skipped: [], reader: null }));
            const emit = makeEmitter(onUpdate, () => ({ kind: "expand_all", phase: "running", total: anchors.length, results: live.slice() }), { intervalMs: emitIntervalMs });
            emit();
            const results = await mapBounded(anchors, 2, (key, i) => expandOne(`${toolCallId}.${i + 1}`, key, args, signal, (partial) => {
                if (partial?.details) live[i] = partial.details;
                emit();
            }).then((res) => { live[i] = res; emit(); return res; }));
            const text = results.map(summarize).join("\n\n");
            const newKeys = results.flatMap((r) => Object.values(r.newByKind || {}).flat());
            return { content: [{ type: "text", text: text || "no anchors given" }], details: { kind: "expand_all", phase: "done", total: anchors.length, results, graph: graphSnapshot(graph, { newKeys }) } };
        },
    };
    return [expandTool, expandAllTool];
}

export const GRAPH_PROMPT_NOTE = [
    "Investigation state:",
    "  - Every analytics_query result (yours and your sub-agents') is folded into a per-chat investigation graph of typed anchors with the evidence class of the records that produced them. `graph_state` shows it; each query result carries a one-line delta. Read the graph before re-querying something it already holds, and pass `key` to get one anchor's neighbourhood.",
    "  - Only when the harness_recipes block lists a recipe whose trigger fits the question, call `run_recipe` with its params before anything else; with no such block nothing has been learned yet, so do not call it: it runs the search and the expansions for you and returns the rows, the anchors and the readers' claims.",
    "  - `expand` investigates one anchor in one call: the harness runs that anchor kind's standard pivots, folds the rows into the graph, and a small reader turns them into claims for your question. `expand_all` does it for a list, one reader per anchor. The moment a search returns the session, endpoint, or process you are after, expand it with your question and the term before writing another query; the reader's claims usually settle the question and the stored rows answer follow-ups. Reach for these before spawning a sub-agent per anchor.",
    "  - `pivot` runs the next query from an anchor you hold without writing the spec: a session's processes or tool calls or conversation content, an endpoint's timeline in a window, a process's DNS or files or connections. It applies the entity's window and row bounds and search rules for you and returns the spec it ran, which is the shape to copy when you do need a hand-written analytics_query. Prefer pivot once you hold an anchor.",
    "  - State conclusions with `claim`, citing anchor keys. An attribution is supported only by an execution record or the model's own tool call; if the claim comes back insufficient, the reason tells you which record to fetch. Build the final answer from supported claims, citing each where the answer rests on it by its id in brackets, as [c3].",
    "  - The graph outlives compaction and reopening the chat, so an investigation can be resumed where it stopped.",
].join("\n");
